import { useEffect, useState } from 'react';

import { useTablePagination } from '@/lib/components/Table/useTablePagination';

import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { useEmblue } from '@/src/infrastructure/Protocol/useEmblue';
import {
  Billing,
  Consumption,
  HansaInvoice,
  PaginationDataResponse,
} from '@/src/modules/MyPlanModule/types';

type TableType =
  | PaginationDataResponse<Consumption[]>
  | PaginationDataResponse<Billing[]>
  | PaginationDataResponse<HansaInvoice[]>;

export const useConsumptionAndBillingTables = <T extends TableType>(
  serviceMethod: 'listConsumption' | 'listBilling' | 'listLegalBilling'
): {
  list: T | undefined;
  listIsLoading: boolean;
  changeOrderBy: (column: { id: string; isAsc: boolean | undefined }) => void;
  changeTablePage: (params: { pageSize: number; pageIndex: number }) => void;
  totalPages: number;
  error: Error | undefined;
} => {
  const [totalList, setTotalList] = useState<number>(0);

  const {
    changeTablePage,
    pageIndex,
    pageSize,
    totalPages,
    changeOrderBy,
    orderColumn,
    orderDirection,
  } = useTablePagination(totalList);

  const getServiceMethod = () => {
    switch (serviceMethod) {
      case 'listConsumption':
        return ServiceMethods.getListConsumption;
      case 'listBilling':
        return ServiceMethods.getListBilling;
      case 'listLegalBilling':
        return ServiceMethods.getListLegalBilling;
    }
  };

  const [list, error, listIsLoading] = useEmblue(getServiceMethod(), {
    limit: pageSize,
    page: pageIndex,
    ...(orderColumn && { sortField: orderColumn }),
    ...(orderDirection && { sortOrder: orderDirection }),
  });

  useEffect(() => {
    if (list) {
      setTotalList((list as TableType).pager.countTotal);
    }
  }, [list]);

  return {
    list: list as T,
    error,
    listIsLoading,
    changeOrderBy,
    changeTablePage,
    totalPages,
  };
};
